import React, { useState, useCallback } from 'react';
import styled from 'styled-components';

// Types
interface ContactData {
  phone?: string;
  city?: string;
  state?: string;
  zip?: string;
  email?: string;
  [key: string]: string | undefined;
}

interface ExperienceData {
  title?: string;
  company?: string;
  start_date?: string;
  end_date?: string;
  description?: string;
}

interface EducationData {
  degree?: string;
  school?: string;
  start_date?: string;
  end_date?: string;
  year?: string;
  field_of_study?: string;
}

interface CertificationData {
  name?: string;
  issuer?: string;
}

interface ParsedResumeData {
  contact?: ContactData;
  summary?: string;
  experience?: ExperienceData[];
  education?: EducationData[];
  skills?: string[];
  certifications?: CertificationData[];
}

interface ResumeImportProps {
  profileId: number;
  resumeParseId: number;
  parsedData: ParsedResumeData;
  csrfToken: string;
  applyUrl: string;
  dismissUrl: string;
}

// Styled Components
const Container = styled.div`
  --ri-primary: #085ec8;
  --ri-primary-light: #e8f2fc;
  --ri-border: #e2e8f0;
  --ri-text: #1e293b;
  --ri-text-muted: #64748b;
  --ri-bg: #ffffff;
  --ri-bg-subtle: #f8fafc;
  margin-bottom: 2rem;
`;

const Banner = styled.div`
  background: linear-gradient(135deg, var(--ri-primary-light) 0%, #f0f7ff 100%);
  border: 1px solid rgba(8, 94, 200, 0.15);
  border-radius: 16px;
  padding: 24px;
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: -50%;
    right: -10%;
    width: 200px;
    height: 200px;
    background: radial-gradient(circle, rgba(8, 94, 200, 0.08) 0%, transparent 70%);
    pointer-events: none;
  }
`;

const BannerContent = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 1rem;
`;

const BannerIcon = styled.div`
  width: 56px;
  height: 56px;
  background: var(--ri-primary);
  border-radius: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 24px;
  box-shadow: 0 4px 12px rgba(8, 94, 200, 0.25);
  flex-shrink: 0;
`;

const BannerText = styled.div`
  flex: 1;
`;

const BannerTitle = styled.h2`
  font-size: 1.25rem;
  font-weight: 700;
  color: var(--ri-text);
  margin: 0 0 4px 0;
  letter-spacing: -0.02em;
`;

const BannerSubtitle = styled.p`
  color: var(--ri-text-muted);
  margin: 0;
  font-size: 0.925rem;
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 0.5rem;
  margin-top: 1rem;
`;

const Button = styled.button<{ variant?: 'primary' | 'ghost' }>`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 10px 20px;
  border-radius: 10px;
  font-weight: 600;
  font-size: 0.9rem;
  cursor: pointer;
  transition: all 0.2s ease;
  border: none;

  ${({ variant }) =>
    variant === 'primary'
      ? `
    background: var(--ri-primary);
    color: white;
    box-shadow: 0 2px 8px rgba(8, 94, 200, 0.25);
    &:hover {
      background: #0747a6;
      transform: translateY(-1px);
    }
  `
      : `
    background: transparent;
    color: var(--ri-text-muted);
    padding: 10px 16px;
    &:hover {
      background: rgba(0,0,0,0.05);
      color: var(--ri-text);
    }
  `}
`;

const Panel = styled.div`
  background: var(--ri-bg);
  border: 1px solid var(--ri-border);
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 4px 24px rgba(0, 0, 0, 0.06);
`;

const PanelHeader = styled.div`
  padding: 20px 24px;
  border-bottom: 1px solid var(--ri-border);
  background: var(--ri-bg-subtle);
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const PanelTitle = styled.h3`
  font-size: 1rem;
  font-weight: 700;
  color: var(--ri-text);
  margin: 0;
  display: flex;
  align-items: center;
  gap: 10px;
`;

const Section = styled.div<{ expanded: boolean }>`
  border-bottom: 1px solid var(--ri-border);

  &:last-of-type {
    border-bottom: none;
  }
`;

const SectionHeader = styled.div`
  padding: 16px 24px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  cursor: pointer;
  transition: background 0.15s ease;
  user-select: none;

  &:hover {
    background: var(--ri-bg-subtle);
  }
`;

const SectionTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  font-weight: 600;
  color: var(--ri-text);
  font-size: 0.95rem;
`;

const SectionIcon = styled.div`
  width: 36px;
  height: 36px;
  background: var(--ri-bg-subtle);
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--ri-primary);
  font-size: 14px;
`;

const SectionCount = styled.span<{ hasError?: boolean }>`
  background: ${({ hasError }) => hasError ? '#fef2f2' : 'var(--ri-primary-light)'};
  color: ${({ hasError }) => hasError ? '#dc2626' : 'var(--ri-primary)'};
  padding: 4px 10px;
  border-radius: 20px;
  font-size: 0.8rem;
  font-weight: 600;
`;

const SectionWarning = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  color: #dc2626;
  font-size: 0.8rem;
  font-weight: 500;
  margin-left: 8px;
`;

const SectionToggle = styled.i<{ expanded: boolean }>`
  color: var(--ri-text-muted);
  transition: transform 0.2s ease;
  transform: ${({ expanded }) => (expanded ? 'rotate(180deg)' : 'rotate(0)')};
`;

const SectionContent = styled.div<{ expanded: boolean }>`
  max-height: ${({ expanded }) => (expanded ? '2000px' : '0')};
  overflow: hidden;
  transition: max-height 0.3s ease;
`;

const SectionInner = styled.div`
  padding: 0 24px 20px 24px;
`;

const SelectActions = styled.div`
  display: flex;
  gap: 8px;
  margin-bottom: 12px;
`;

const SelectButton = styled.button`
  padding: 6px 12px;
  border-radius: 6px;
  font-size: 0.8rem;
  font-weight: 500;
  cursor: pointer;
  border: 1px solid var(--ri-border);
  background: var(--ri-bg);
  color: var(--ri-text-muted);
  transition: all 0.15s ease;

  &:hover {
    border-color: var(--ri-primary);
    color: var(--ri-primary);
  }
`;

const Card = styled.div<{ selected: boolean }>`
  background: ${({ selected }) => (selected ? 'var(--ri-primary-light)' : 'var(--ri-bg-subtle)')};
  border: 1px solid ${({ selected }) => (selected ? 'var(--ri-primary)' : 'var(--ri-border)')};
  border-radius: 12px;
  padding: 16px;
  margin-bottom: 12px;
  display: flex;
  gap: 14px;
  transition: all 0.15s ease;
  cursor: pointer;

  &:last-child {
    margin-bottom: 0;
  }

  &:hover {
    border-color: var(--ri-primary);
    background: var(--ri-primary-light);
  }
`;

const CardCheck = styled.div<{ selected: boolean }>`
  flex-shrink: 0;
  width: 22px;
  height: 22px;
  border: 2px solid ${({ selected }) => (selected ? 'var(--ri-primary)' : 'var(--ri-border)')};
  background: ${({ selected }) => (selected ? 'var(--ri-primary)' : 'transparent')};
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.15s ease;
  margin-top: 2px;
  color: white;
`;

const CardContent = styled.div`
  flex: 1;
  min-width: 0;
`;

const CardTitle = styled.div`
  font-weight: 600;
  color: var(--ri-text);
  margin-bottom: 2px;
  font-size: 0.95rem;
`;

const CardSubtitle = styled.div`
  color: var(--ri-text-muted);
  font-size: 0.875rem;
  margin-bottom: 6px;
`;

const CardMeta = styled.div`
  color: var(--ri-text-muted);
  font-size: 0.8rem;
  display: flex;
  align-items: center;
  gap: 6px;
`;

const CardDesc = styled.div`
  color: var(--ri-text-muted);
  font-size: 0.85rem;
  margin-top: 8px;
  line-height: 1.5;
`;

const SkillsGrid = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;

const SkillChip = styled.div<{ selected: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 14px;
  background: ${({ selected }) => (selected ? 'var(--ri-primary)' : 'var(--ri-bg-subtle)')};
  border: 1px solid ${({ selected }) => (selected ? 'var(--ri-primary)' : 'var(--ri-border)')};
  border-radius: 20px;
  font-size: 0.85rem;
  color: ${({ selected }) => (selected ? 'white' : 'var(--ri-text)')};
  cursor: pointer;
  transition: all 0.15s ease;
  user-select: none;

  &:hover {
    border-color: var(--ri-primary);
  }

  svg {
    width: 14px;
    height: 14px;
    opacity: ${({ selected }) => (selected ? 1 : 0)};
    transform: ${({ selected }) => (selected ? 'scale(1)' : 'scale(0.5)')};
    transition: all 0.15s ease;
  }
`;

const ContactRow = styled.div<{ selected: boolean }>`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 14px;
  background: ${({ selected }) => (selected ? 'var(--ri-primary-light)' : 'var(--ri-bg-subtle)')};
  border: 1px solid ${({ selected }) => (selected ? 'var(--ri-primary)' : 'var(--ri-border)')};
  border-radius: 10px;
  margin-bottom: 8px;
  cursor: pointer;
  transition: all 0.15s ease;

  &:hover {
    border-color: var(--ri-primary);
    background: var(--ri-primary-light);
  }

  &:last-child {
    margin-bottom: 0;
  }
`;

const ContactLabel = styled.span`
  font-weight: 500;
  color: var(--ri-text-muted);
  font-size: 0.8rem;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  min-width: 70px;
`;

const ContactValue = styled.span`
  color: var(--ri-text);
  font-size: 0.925rem;
  flex: 1;
`;

const SummaryText = styled.div<{ selected: boolean }>`
  padding: 14px 16px;
  background: ${({ selected }) => (selected ? 'var(--ri-primary-light)' : 'var(--ri-bg-subtle)')};
  border: 1px solid ${({ selected }) => (selected ? 'var(--ri-primary)' : 'var(--ri-border)')};
  border-radius: 10px;
  color: var(--ri-text);
  font-size: 0.9rem;
  line-height: 1.6;
  cursor: pointer;
  transition: all 0.15s ease;
  display: flex;
  gap: 12px;

  &:hover {
    border-color: var(--ri-primary);
    background: var(--ri-primary-light);
  }
`;

const ActionsBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 16px 24px;
  background: var(--ri-bg-subtle);
  border-top: 1px solid var(--ri-border);

  @media (max-width: 640px) {
    flex-direction: column;
    gap: 12px;

    button {
      width: 100%;
      justify-content: center;
    }
  }
`;

// Helper function
const truncate = (str: string, length: number): string => {
  if (str.length <= length) return str;
  return str.slice(0, length) + '...';
};

// Validation helpers
const isValidDate = (dateStr: string | undefined): boolean => {
  if (!dateStr) return false;
  if (dateStr.toLowerCase().includes('present')) return true;
  // Check for various date formats
  const patterns = [
    /^\d{4}-\d{2}$/, // 2020-01
    /^\d{4}$/, // 2020
    /^\w+\s+\d{4}$/, // January 2020
    /^\d{2}\/\d{4}$/, // 01/2020
  ];
  return patterns.some(p => p.test(dateStr)) || !isNaN(Date.parse(dateStr));
};

const validateExperience = (exp: ExperienceData): string[] => {
  const errors: string[] = [];
  if (!exp.title?.trim()) errors.push('Missing job title');
  if (!exp.company?.trim()) errors.push('Missing company');
  if (!exp.start_date || !isValidDate(exp.start_date)) errors.push('Missing or invalid start date');
  return errors;
};

const validateEducation = (edu: EducationData): string[] => {
  const errors: string[] = [];
  if (!edu.school?.trim()) errors.push('Missing school name');
  if (!edu.degree?.trim()) errors.push('Missing degree');
  const hasValidDate = isValidDate(edu.start_date) || isValidDate(edu.year) || isValidDate(edu.end_date);
  if (!hasValidDate) errors.push('Missing graduation year or dates');
  return errors;
};

// Component
const ResumeImport: React.FC<ResumeImportProps> = ({
  resumeParseId,
  parsedData,
  csrfToken,
  applyUrl,
  dismissUrl,
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDismissed, setIsDismissed] = useState(false);

  // Section expansion states
  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({
    contact: true,
    summary: true,
    experience: true,
    education: true,
    skills: true,
    certifications: true,
  });

  // Selection states
  const [selectedContact, setSelectedContact] = useState<Record<string, boolean>>(() => {
    const initial: Record<string, boolean> = {};
    if (parsedData.contact) {
      Object.keys(parsedData.contact)
        .filter((k) => k !== 'email' && parsedData.contact![k])
        .forEach((k) => {
          initial[k] = true;
        });
    }
    return initial;
  });

  const [selectedSummary, setSelectedSummary] = useState(!!parsedData.summary);

  const [selectedExperience, setSelectedExperience] = useState<boolean[]>(
    () => parsedData.experience?.map(() => true) || []
  );

  const [selectedEducation, setSelectedEducation] = useState<boolean[]>(
    () => parsedData.education?.map(() => true) || []
  );

  const [selectedSkills, setSelectedSkills] = useState<boolean[]>(
    () => parsedData.skills?.map(() => true) || []
  );

  const [selectedCertifications, setSelectedCertifications] = useState<boolean[]>(
    () => parsedData.certifications?.map(() => true) || []
  );

  // Compute validation errors
  const experienceErrors = React.useMemo(() =>
    parsedData.experience?.map(exp => validateExperience(exp)) || [],
    [parsedData.experience]
  );

  const educationErrors = React.useMemo(() =>
    parsedData.education?.map(edu => validateEducation(edu)) || [],
    [parsedData.education]
  );

  // Count items with errors that are selected
  const selectedExperienceErrorCount = experienceErrors.filter(
    (errors, idx) => errors.length > 0 && selectedExperience[idx]
  ).length;

  const selectedEducationErrorCount = educationErrors.filter(
    (errors, idx) => errors.length > 0 && selectedEducation[idx]
  ).length;

  // Filter out email from contact
  const contactFields = parsedData.contact
    ? Object.entries(parsedData.contact).filter(([key, value]) => key !== 'email' && value)
    : [];

  const hasContact = contactFields.length > 0;
  const hasSummary = !!parsedData.summary;
  const hasExperience = parsedData.experience && parsedData.experience.length > 0;
  const hasEducation = parsedData.education && parsedData.education.length > 0;
  const hasSkills = parsedData.skills && parsedData.skills.length > 0;
  const hasCertifications = parsedData.certifications && parsedData.certifications.length > 0;

  const toggleSection = useCallback((section: string) => {
    setExpandedSections((prev) => ({ ...prev, [section]: !prev[section] }));
  }, []);

  const handleSubmit = async () => {
    setIsSubmitting(true);

    const formData = new FormData();
    formData.append('resume_parse_id', resumeParseId.toString());

    // Add selected contact fields
    contactFields.forEach(([field, value]) => {
      if (selectedContact[field] && value) {
        formData.append(`fields[contact][${field}]`, value);
      }
    });

    // Add summary
    if (selectedSummary && parsedData.summary) {
      formData.append('fields[summary]', parsedData.summary);
    }

    // Add selected experiences
    selectedExperience.forEach((selected, index) => {
      if (selected) {
        formData.append(`fields[experience][${index}]`, index.toString());
      }
    });

    // Add selected education
    selectedEducation.forEach((selected, index) => {
      if (selected) {
        formData.append(`fields[education][${index}]`, index.toString());
      }
    });

    // Add selected skills
    selectedSkills.forEach((selected, index) => {
      if (selected && parsedData.skills) {
        formData.append(`fields[skills][${index}]`, parsedData.skills[index]);
      }
    });

    // Add selected certifications
    selectedCertifications.forEach((selected, index) => {
      if (selected) {
        formData.append(`fields[certifications][${index}]`, index.toString());
      }
    });

    try {
      const response = await fetch(applyUrl, {
        method: 'POST',
        headers: {
          'X-CSRF-Token': csrfToken,
        },
        body: formData,
      });

      if (response.ok) {
        window.location.reload();
      }
    } catch (error) {
      console.error('Failed to apply resume data:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDismiss = async () => {
    try {
      await fetch(dismissUrl, {
        method: 'POST',
        headers: {
          'X-CSRF-Token': csrfToken,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ resume_parse_id: resumeParseId }),
      });
      setIsDismissed(true);
    } catch (error) {
      console.error('Failed to dismiss:', error);
    }
  };

  if (isDismissed) return null;

  return (
    <Container>
      {!isExpanded ? (
        <Banner>
          <BannerContent>
            <BannerIcon>
              <i className="fas fa-file-import" />
            </BannerIcon>
            <BannerText>
              <BannerTitle>Import Resume Data</BannerTitle>
              <BannerSubtitle>
                We extracted information from your resume. Review and add it to your profile.
              </BannerSubtitle>
              <ButtonGroup>
                <Button variant="primary" onClick={() => setIsExpanded(true)}>
                  <i className="fas fa-sparkles" />
                  Review Data
                </Button>
                <Button onClick={handleDismiss}>Dismiss</Button>
              </ButtonGroup>
            </BannerText>
          </BannerContent>
        </Banner>
      ) : (
        <Panel>
          <PanelHeader>
            <PanelTitle>
              <i className="fas fa-file-import" style={{ color: 'var(--ri-primary)' }} />
              Select Data to Import
            </PanelTitle>
            <Button onClick={() => setIsExpanded(false)} style={{ padding: '8px 12px' }}>
              <i className="fas fa-times" />
            </Button>
          </PanelHeader>

          {hasContact && (
            <Section expanded={expandedSections.contact}>
              <SectionHeader onClick={() => toggleSection('contact')}>
                <SectionTitle>
                  <SectionIcon>
                    <i className="fas fa-user" />
                  </SectionIcon>
                  Contact Information
                  <SectionCount>{contactFields.length}</SectionCount>
                </SectionTitle>
                <SectionToggle className="fas fa-chevron-down" expanded={expandedSections.contact} />
              </SectionHeader>
              <SectionContent expanded={expandedSections.contact}>
                <SectionInner>
                  {contactFields.map(([field, value]) => (
                    <ContactRow
                      key={field}
                      selected={selectedContact[field]}
                      onClick={() =>
                        setSelectedContact((prev) => ({ ...prev, [field]: !prev[field] }))
                      }
                    >
                      <CardCheck selected={selectedContact[field]}>
                        {selectedContact[field] && <i className="fas fa-check fa-xs" />}
                      </CardCheck>
                      <ContactLabel>{field.replace(/_/g, ' ')}</ContactLabel>
                      <ContactValue>{value}</ContactValue>
                    </ContactRow>
                  ))}
                </SectionInner>
              </SectionContent>
            </Section>
          )}

          {hasSummary && (
            <Section expanded={expandedSections.summary}>
              <SectionHeader onClick={() => toggleSection('summary')}>
                <SectionTitle>
                  <SectionIcon>
                    <i className="fas fa-align-left" />
                  </SectionIcon>
                  Professional Summary
                </SectionTitle>
                <SectionToggle className="fas fa-chevron-down" expanded={expandedSections.summary} />
              </SectionHeader>
              <SectionContent expanded={expandedSections.summary}>
                <SectionInner>
                  <SummaryText
                    selected={selectedSummary}
                    onClick={() => setSelectedSummary(!selectedSummary)}
                  >
                    <CardCheck selected={selectedSummary}>
                      {selectedSummary && <i className="fas fa-check fa-xs" />}
                    </CardCheck>
                    <div>{parsedData.summary}</div>
                  </SummaryText>
                </SectionInner>
              </SectionContent>
            </Section>
          )}

          {hasExperience && (
            <Section expanded={expandedSections.experience}>
              <SectionHeader onClick={() => toggleSection('experience')}>
                <SectionTitle>
                  <SectionIcon>
                    <i className="fas fa-briefcase" />
                  </SectionIcon>
                  Work Experience
                  <SectionCount hasError={selectedExperienceErrorCount > 0}>
                    {parsedData.experience!.length}
                  </SectionCount>
                  {selectedExperienceErrorCount > 0 && (
                    <SectionWarning>
                      <i className="fas fa-exclamation-triangle" />
                      {selectedExperienceErrorCount} with issues
                    </SectionWarning>
                  )}
                </SectionTitle>
                <SectionToggle
                  className="fas fa-chevron-down"
                  expanded={expandedSections.experience}
                />
              </SectionHeader>
              <SectionContent expanded={expandedSections.experience}>
                <SectionInner>
                  <SelectActions>
                    <SelectButton
                      onClick={() => setSelectedExperience(parsedData.experience!.map(() => true))}
                    >
                      Select All
                    </SelectButton>
                    <SelectButton
                      onClick={() => setSelectedExperience(parsedData.experience!.map(() => false))}
                    >
                      Deselect All
                    </SelectButton>
                  </SelectActions>
                  {parsedData.experience!.map((exp, index) => (
                    <Card
                      key={index}
                      selected={selectedExperience[index]}
                      onClick={() =>
                        setSelectedExperience((prev) => {
                          const next = [...prev];
                          next[index] = !next[index];
                          return next;
                        })
                      }
                    >
                      <CardCheck selected={selectedExperience[index]}>
                        {selectedExperience[index] && <i className="fas fa-check fa-xs" />}
                      </CardCheck>
                      <CardContent>
                        <CardTitle>{exp.title || 'Untitled Position'}</CardTitle>
                        {exp.company && <CardSubtitle>{exp.company}</CardSubtitle>}
                        <CardMeta>
                          <i className="far fa-calendar" />
                          {exp.start_date || 'N/A'} &mdash; {exp.end_date || 'Present'}
                        </CardMeta>
                        {exp.description && <CardDesc>{truncate(exp.description, 180)}</CardDesc>}
                      </CardContent>
                    </Card>
                  ))}
                </SectionInner>
              </SectionContent>
            </Section>
          )}

          {hasEducation && (
            <Section expanded={expandedSections.education}>
              <SectionHeader onClick={() => toggleSection('education')}>
                <SectionTitle>
                  <SectionIcon>
                    <i className="fas fa-graduation-cap" />
                  </SectionIcon>
                  Education
                  <SectionCount hasError={selectedEducationErrorCount > 0}>
                    {parsedData.education!.length}
                  </SectionCount>
                  {selectedEducationErrorCount > 0 && (
                    <SectionWarning>
                      <i className="fas fa-exclamation-triangle" />
                      {selectedEducationErrorCount} with issues
                    </SectionWarning>
                  )}
                </SectionTitle>
                <SectionToggle
                  className="fas fa-chevron-down"
                  expanded={expandedSections.education}
                />
              </SectionHeader>
              <SectionContent expanded={expandedSections.education}>
                <SectionInner>
                  <SelectActions>
                    <SelectButton
                      onClick={() => setSelectedEducation(parsedData.education!.map(() => true))}
                    >
                      Select All
                    </SelectButton>
                    <SelectButton
                      onClick={() => setSelectedEducation(parsedData.education!.map(() => false))}
                    >
                      Deselect All
                    </SelectButton>
                  </SelectActions>
                  {parsedData.education!.map((edu, index) => (
                    <Card
                      key={index}
                      selected={selectedEducation[index]}
                      onClick={() =>
                        setSelectedEducation((prev) => {
                          const next = [...prev];
                          next[index] = !next[index];
                          return next;
                        })
                      }
                    >
                      <CardCheck selected={selectedEducation[index]}>
                        {selectedEducation[index] && <i className="fas fa-check fa-xs" />}
                      </CardCheck>
                      <CardContent>
                        <CardTitle>{edu.degree || 'Degree'}</CardTitle>
                        {edu.school && <CardSubtitle>{edu.school}</CardSubtitle>}
                        <CardMeta>
                          <i className="far fa-calendar" />
                          {edu.start_date || edu.year || 'N/A'}
                          {edu.end_date && ` — ${edu.end_date}`}
                        </CardMeta>
                        {edu.field_of_study && <CardDesc>{edu.field_of_study}</CardDesc>}
                      </CardContent>
                    </Card>
                  ))}
                </SectionInner>
              </SectionContent>
            </Section>
          )}

          {hasSkills && (
            <Section expanded={expandedSections.skills}>
              <SectionHeader onClick={() => toggleSection('skills')}>
                <SectionTitle>
                  <SectionIcon>
                    <i className="fas fa-tools" />
                  </SectionIcon>
                  Skills
                  <SectionCount>{parsedData.skills!.length}</SectionCount>
                </SectionTitle>
                <SectionToggle className="fas fa-chevron-down" expanded={expandedSections.skills} />
              </SectionHeader>
              <SectionContent expanded={expandedSections.skills}>
                <SectionInner>
                  <SelectActions>
                    <SelectButton
                      onClick={() => setSelectedSkills(parsedData.skills!.map(() => true))}
                    >
                      Select All
                    </SelectButton>
                    <SelectButton
                      onClick={() => setSelectedSkills(parsedData.skills!.map(() => false))}
                    >
                      Deselect All
                    </SelectButton>
                  </SelectActions>
                  <SkillsGrid>
                    {parsedData.skills!.map((skill, index) => (
                      <SkillChip
                        key={index}
                        selected={selectedSkills[index]}
                        onClick={() =>
                          setSelectedSkills((prev) => {
                            const next = [...prev];
                            next[index] = !next[index];
                            return next;
                          })
                        }
                      >
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                          <polyline points="20 6 9 17 4 12" />
                        </svg>
                        {skill}
                      </SkillChip>
                    ))}
                  </SkillsGrid>
                </SectionInner>
              </SectionContent>
            </Section>
          )}

          {hasCertifications && (
            <Section expanded={expandedSections.certifications}>
              <SectionHeader onClick={() => toggleSection('certifications')}>
                <SectionTitle>
                  <SectionIcon>
                    <i className="fas fa-certificate" />
                  </SectionIcon>
                  Certifications
                  <SectionCount>{parsedData.certifications!.length}</SectionCount>
                </SectionTitle>
                <SectionToggle
                  className="fas fa-chevron-down"
                  expanded={expandedSections.certifications}
                />
              </SectionHeader>
              <SectionContent expanded={expandedSections.certifications}>
                <SectionInner>
                  <SelectActions>
                    <SelectButton
                      onClick={() =>
                        setSelectedCertifications(parsedData.certifications!.map(() => true))
                      }
                    >
                      Select All
                    </SelectButton>
                    <SelectButton
                      onClick={() =>
                        setSelectedCertifications(parsedData.certifications!.map(() => false))
                      }
                    >
                      Deselect All
                    </SelectButton>
                  </SelectActions>
                  {parsedData.certifications!.map((cert, index) => (
                    <Card
                      key={index}
                      selected={selectedCertifications[index]}
                      onClick={() =>
                        setSelectedCertifications((prev) => {
                          const next = [...prev];
                          next[index] = !next[index];
                          return next;
                        })
                      }
                    >
                      <CardCheck selected={selectedCertifications[index]}>
                        {selectedCertifications[index] && <i className="fas fa-check fa-xs" />}
                      </CardCheck>
                      <CardContent>
                        <CardTitle>
                          {typeof cert === 'string' ? cert : cert.name || 'Certification'}
                        </CardTitle>
                        {typeof cert !== 'string' && cert.issuer && (
                          <CardSubtitle>{cert.issuer}</CardSubtitle>
                        )}
                      </CardContent>
                    </Card>
                  ))}
                </SectionInner>
              </SectionContent>
            </Section>
          )}

          <ActionsBar>
            <Button onClick={() => setIsExpanded(false)}>Cancel</Button>
            <Button variant="primary" onClick={handleSubmit} disabled={isSubmitting}>
              {isSubmitting ? (
                <>
                  <span className="spinner-border spinner-border-sm" />
                  Applying...
                </>
              ) : (
                <>
                  <i className="fas fa-check" />
                  Apply Selected Data
                </>
              )}
            </Button>
          </ActionsBar>
        </Panel>
      )}
    </Container>
  );
};

export default ResumeImport;
